/**
 * Billing arrangements: invoice runs for directory accounts, outside workspaces.
 *
 * An arrangement owns one minted reference (e.g. KSU-0001). Its invoices are
 * ordinary Invoice documents on the same organisation + reference, so they are
 * numbered KSU-0001-01, -02 … and use the same deposit / works ledger as jobs.
 * Amounts in the ledger are gross (VAT-inclusive where VAT is charged).
 */
import { invoicedToDate, normalizeInvoiceRole, type InvoiceRole, type LedgerInvoice } from '../docs/utils/invoiceLedger';
import { CIS_BAND_RATES, type CisBand } from './accounts.model';
import {
  billingPeriod,
  normaliseFrequency,
  periodLabel,
  recurringStatus,
  todayIso,
  type BillingFrequency,
  type BillingPeriod,
  type RecurringStatus,
} from './recurrence.model';

/** The nature of the arrangement. A company can have arrangements of different types. */
export type ArrangementType = 'partner_trade' | 'tenant' | 'supplier' | 'subcontractor' | 'other';

export const ARRANGEMENT_TYPE_LABELS: Record<ArrangementType, string> = {
  partner_trade: 'Partner trade',
  tenant: 'Tenant',
  supplier: 'Supplier',
  subcontractor: 'Subcontractor',
  other: 'Other',
};

const ARRANGEMENT_TYPES = new Set<ArrangementType>(['partner_trade', 'tenant', 'supplier', 'subcontractor', 'other']);

export function normaliseArrangementType(value: unknown): ArrangementType {
  return ARRANGEMENT_TYPES.has(value as ArrangementType) ? value as ArrangementType : 'other';
}

export type VatTreatment = 'standard' | 'reverse_charge' | 'exempt' | 'outside_scope';

export const VAT_TREATMENT_LABELS: Record<VatTreatment, string> = {
  standard: 'Standard VAT',
  reverse_charge: 'Domestic reverse charge',
  exempt: 'Exempt',
  outside_scope: 'Outside the scope of VAT',
};

export type ArrangementStatus = 'open' | 'closed';

export type BillingArrangement = {
  id: string;
  organisationId: string;
  lifecycleRef: string;
  accountId: string;
  arrangementType: ArrangementType;
  title: string;
  description: string | null;
  /** Agreed total including any VAT charged. Null for open-ended arrangements such as rent. */
  agreedGross: number | null;
  vatTreatment: VatTreatment;
  /** 0..1 */
  vatPct: number;
  /** 0..1 of the agreed total. Null means no deposit. */
  depositPercentage: number | null;
  paymentTermsInDays: number;
  /** `none` = invoice as needed. Otherwise one invoice per period, raised in advance. */
  frequency: BillingFrequency;
  /** Amount per period, excluding VAT. */
  recurringNet: number | null;
  /** yyyy-MM-dd. Periods are counted from here. */
  firstPeriodStart: string | null;
  /** Next period to invoice, 0 = first. */
  nextPeriodIndex: number;
  /** yyyy-MM-dd. No periods start after this. */
  endsOn: string | null;
  /** CIS the customer deducts from our invoices, at Keystone's rate. `none` = no CIS. */
  cisBand: CisBand;
  status: ArrangementStatus;
  createdAt: string;
};

export type BillingArrangementInput = {
  accountId: string;
  arrangementType?: ArrangementType;
  title: string;
  description?: string | null;
  /** Agreed total excluding VAT, as typed in. Null for open-ended. */
  agreedNet: number | null;
  vatTreatment: VatTreatment;
  vatPct?: number;
  depositPercentage?: number | null;
  paymentTermsInDays?: number;
  frequency?: BillingFrequency;
  recurringNet?: number | null;
  firstPeriodStart?: string | null;
  endsOn?: string | null;
  cisBand?: CisBand;
};

/** What the office asks for on the Invoicing page. */
export type ArrangementInvoiceKind = 'deposit' | 'interim' | 'balance' | 'one_off' | 'recurring';

export const ARRANGEMENT_INVOICE_LABELS: Record<ArrangementInvoiceKind, string> = {
  deposit: 'Deposit',
  interim: 'Interim',
  balance: 'Balance',
  one_off: 'Invoice',
  recurring: 'Recurring',
};

export type ArrangementInvoicePlan = {
  kind: ArrangementInvoiceKind;
  role: InvoiceRole;
  /** VAT-inclusive where VAT is charged, otherwise equal to net. */
  amountGross: number;
  amountNet: number;
  vatAmount: number;
  vatEnabled: boolean;
  title: string;
  lineName: string;
  sourceEvent: string;
  frozen: boolean;
  /** Set on recurring invoices: the period being billed. */
  period?: BillingPeriod;
  /** Set when the customer deducts CIS from this invoice. */
  cis?: InvoiceCis;
};

export type InvoiceCis = {
  band: Exclude<CisBand, 'none'>;
  rate: number;
  /** Labour in this invoice, ex VAT. CIS is never taken from materials or VAT. */
  labourNet: number;
  deduction: number;
  /** What the customer pays after deducting CIS. */
  payableAfterCis: number;
};

const VAT_TREATMENTS = new Set<VatTreatment>(['standard', 'reverse_charge', 'exempt', 'outside_scope']);

export function money(value: number): number {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

function optionalNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function normaliseVatTreatment(value: unknown): VatTreatment {
  return VAT_TREATMENTS.has(value as VatTreatment) ? value as VatTreatment : 'standard';
}

/** Only standard-rated arrangements charge VAT on the invoice. */
export function chargesVat(treatment: VatTreatment): boolean {
  return treatment === 'standard';
}

export function grossFromNet(net: number, treatment: VatTreatment, vatPct: number): number {
  return chargesVat(treatment) ? money(net * (1 + vatPct)) : money(net);
}

export function netFromGross(gross: number, treatment: VatTreatment, vatPct: number): number {
  return chargesVat(treatment) ? money(gross / (1 + vatPct)) : money(gross);
}

export function fromDbArrangement(row: Record<string, any>): BillingArrangement {
  return {
    id: String(row.id),
    organisationId: String(row.organisation_id),
    lifecycleRef: String(row.lifecycle_ref || ''),
    accountId: String(row.account_id),
    arrangementType: normaliseArrangementType(row.arrangement_type),
    title: String(row.title || ''),
    description: row.description ?? null,
    agreedGross: optionalNumber(row.agreed_gross),
    vatTreatment: normaliseVatTreatment(row.vat_treatment),
    vatPct: optionalNumber(row.vat_pct) ?? 0.2,
    depositPercentage: optionalNumber(row.deposit_percentage),
    paymentTermsInDays: optionalNumber(row.payment_terms_in_days) ?? 7,
    frequency: normaliseFrequency(row.frequency),
    recurringNet: optionalNumber(row.recurring_net),
    firstPeriodStart: row.first_period_start ? String(row.first_period_start).slice(0, 10) : null,
    nextPeriodIndex: optionalNumber(row.next_period_index) ?? 0,
    endsOn: row.ends_on ? String(row.ends_on).slice(0, 10) : null,
    cisBand: normaliseCisBand(row.cis_band),
    status: row.status === 'closed' ? 'closed' : 'open',
    createdAt: String(row.created_at || ''),
  };
}

/** Returns the first problem with the input, or null when it can be saved. */
export function validateArrangementInput(input: BillingArrangementInput): string | null {
  if (!String(input.accountId || '').trim()) return 'Choose the account to invoice.';
  if (!String(input.title || '').trim()) return 'Give the arrangement a title.';
  if (input.agreedNet != null && !(Number(input.agreedNet) > 0)) return 'The agreed amount must be more than zero.';
  const deposit = input.depositPercentage;
  if (deposit != null) {
    if (input.agreedNet == null) return 'A deposit needs an agreed amount to be a percentage of.';
    if (!(deposit > 0 && deposit <= 1)) return 'The deposit must be between 1% and 100%.';
  }
  const vatPct = input.vatPct ?? 0.2;
  if (!(vatPct >= 0 && vatPct < 1)) return 'The VAT rate must be between 0% and 100%.';
  const terms = input.paymentTermsInDays ?? 7;
  if (!(Number.isInteger(terms) && terms >= 0)) return 'Payment terms must be a whole number of days.';
  if (normaliseFrequency(input.frequency) !== 'none') {
    if (input.agreedNet != null) return 'A recurring arrangement bills an amount per period, so leave the agreed amount blank.';
    if (!(Number(input.recurringNet) > 0)) return 'Enter the amount for each period.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(input.firstPeriodStart || ''))) return 'Enter the date the first period starts.';
    if (input.endsOn && input.endsOn < String(input.firstPeriodStart)) return 'The end date is before the first period starts.';
  }
  return null;
}

export function toDbArrangement(input: BillingArrangementInput): Record<string, unknown> {
  const vatTreatment = normaliseVatTreatment(input.vatTreatment);
  const vatPct = input.vatPct ?? 0.2;
  return {
    account_id: input.accountId,
    arrangement_type: normaliseArrangementType(input.arrangementType),
    title: input.title.trim(),
    description: String(input.description || '').trim() || null,
    agreed_gross: input.agreedNet == null ? null : grossFromNet(Number(input.agreedNet), vatTreatment, vatPct),
    vat_treatment: vatTreatment,
    vat_pct: vatPct,
    deposit_percentage: input.depositPercentage ?? null,
    payment_terms_in_days: input.paymentTermsInDays ?? 7,
    cis_band: normaliseCisBand(input.cisBand),
    ...recurringColumns(input),
  };
}

export function normaliseCisBand(value: unknown): CisBand {
  return value === 'gross' || value === 'standard' || value === 'higher' ? value : 'none';
}

function recurringColumns(input: BillingArrangementInput): Record<string, unknown> {
  const frequency = normaliseFrequency(input.frequency);
  if (frequency === 'none') {
    return { frequency, recurring_net: null, first_period_start: null, ends_on: null };
  }
  return {
    frequency,
    recurring_net: money(Number(input.recurringNet)),
    first_period_start: input.firstPeriodStart,
    ends_on: input.endsOn || null,
  };
}

/** The next period to invoice, or null when the arrangement is not recurring or has ended. */
export function nextBillingPeriod(arrangement: BillingArrangement): BillingPeriod | null {
  if (arrangement.frequency === 'none' || !arrangement.firstPeriodStart) return null;
  const period = billingPeriod(arrangement.firstPeriodStart, arrangement.frequency, arrangement.nextPeriodIndex);
  if (arrangement.endsOn && period.start > arrangement.endsOn) return null;
  return period;
}

/** Due, upcoming or ended, for recurring arrangements that are open. Null otherwise. */
export function arrangementRecurringStatus(arrangement: BillingArrangement, today = todayIso()): RecurringStatus | null {
  if (arrangement.status !== 'open' || arrangement.frequency === 'none' || !arrangement.firstPeriodStart) return null;
  const period = billingPeriod(arrangement.firstPeriodStart, arrangement.frequency, arrangement.nextPeriodIndex);
  return recurringStatus({ nextPeriodStart: period.start, endsOn: arrangement.endsOn, today });
}

function isLive(invoice: LedgerInvoice): boolean {
  return String(invoice.status || '').trim().toLowerCase() !== 'void';
}

export type ArrangementLedger = {
  agreedGross: number | null;
  invoicedGross: number;
  remainingGross: number | null;
  hasDeposit: boolean;
  liveCount: number;
};

export function arrangementLedger(arrangement: BillingArrangement, invoices: LedgerInvoice[]): ArrangementLedger {
  const live = invoices.filter(isLive);
  const invoicedGross = money(invoicedToDate(invoices));
  return {
    agreedGross: arrangement.agreedGross,
    invoicedGross,
    remainingGross: arrangement.agreedGross == null ? null : money(arrangement.agreedGross - invoicedGross),
    hasDeposit: live.some((invoice) => normalizeInvoiceRole(invoice.role) === 'deposit'),
    liveCount: live.length,
  };
}

/** Which invoice kinds the page should offer right now. */
export function availableInvoiceKinds(
  arrangement: BillingArrangement,
  invoices: LedgerInvoice[],
): ArrangementInvoiceKind[] {
  if (arrangement.status === 'closed') return [];
  if (arrangement.agreedGross == null) {
    return nextBillingPeriod(arrangement) ? ['recurring', 'one_off'] : ['one_off'];
  }
  const ledger = arrangementLedger(arrangement, invoices);
  const kinds: ArrangementInvoiceKind[] = [];
  if (arrangement.depositPercentage != null && ledger.liveCount === 0) kinds.push('deposit');
  if ((ledger.remainingGross ?? 0) > 0) kinds.push('interim', 'balance');
  return kinds;
}

/**
 * Works out the next invoice for an arrangement.
 * `amountNet` is the ex-VAT figure typed in for interim and one-off invoices.
 * Throws with a message the office can act on when the request does not fit.
 */
export function planArrangementInvoice(args: {
  arrangement: BillingArrangement;
  invoices: LedgerInvoice[];
  kind: ArrangementInvoiceKind;
  amountNet?: number | null;
  lineName?: string | null;
  /** Labour in this invoice, ex VAT, when CIS applies. Defaults to the whole net amount. */
  cisLabourNet?: number | null;
}): ArrangementInvoicePlan {
  const { arrangement, invoices, kind } = args;
  if (arrangement.status === 'closed') throw new Error('This arrangement is closed.');
  if (!availableInvoiceKinds(arrangement, invoices).includes(kind)) {
    if (kind === 'deposit') throw new Error('A deposit can only be the first invoice on an arrangement with a deposit set.');
    if (kind === 'one_off') throw new Error('This arrangement has an agreed total. Use an interim or balance invoice.');
    if (kind === 'recurring') throw new Error('There is no further period to invoice on this arrangement.');
    if (arrangement.agreedGross == null) throw new Error('This arrangement has no agreed total. Use a one-off invoice.');
    throw new Error('The agreed total has already been invoiced.');
  }

  const treatment = arrangement.vatTreatment;
  const vatEnabled = chargesVat(treatment);
  const ledger = arrangementLedger(arrangement, invoices);
  let amountGross: number;

  const period = kind === 'recurring' ? nextBillingPeriod(arrangement) as BillingPeriod : undefined;

  if (kind === 'recurring') {
    amountGross = grossFromNet(arrangement.recurringNet as number, treatment, arrangement.vatPct);
  } else if (kind === 'deposit') {
    amountGross = money((arrangement.agreedGross as number) * (arrangement.depositPercentage as number));
  } else if (kind === 'balance') {
    amountGross = ledger.remainingGross as number;
  } else {
    const net = Number(args.amountNet);
    if (!(net > 0)) throw new Error('Enter an amount greater than zero.');
    amountGross = grossFromNet(net, treatment, arrangement.vatPct);
    if (kind === 'interim') {
      const remaining = ledger.remainingGross as number;
      if (amountGross > remaining + 0.005) {
        throw new Error('That is more than is left to invoice. Use a balance invoice for the remainder.');
      }
      if (Math.abs(amountGross - remaining) < 0.005) {
        throw new Error('That clears what is left. Use a balance invoice instead.');
      }
    }
  }

  if (!(amountGross > 0)) throw new Error('There is nothing to invoice.');
  const amountNet = netFromGross(amountGross, treatment, arrangement.vatPct);
  const cis = invoiceCis({
    band: arrangement.cisBand,
    amountNet,
    amountGross,
    labourNet: args.cisLabourNet,
  });
  const label = ARRANGEMENT_INVOICE_LABELS[kind];
  const sequence = ledger.liveCount + 1;
  if (period) {
    const when = periodLabel(period);
    const base = String(args.lineName || '').trim() || arrangement.title;
    return {
      kind,
      role: 'works',
      amountGross,
      amountNet,
      vatAmount: money(amountGross - amountNet),
      vatEnabled,
      title: `${arrangement.title} — ${when}`,
      lineName: `${base} (${when})`,
      sourceEvent: `arrangement:${arrangement.id}:recurring:${period.index}:${period.start}`,
      frozen: false,
      period,
      ...(cis ? { cis } : {}),
    };
  }
  return {
    kind,
    role: kind === 'deposit' ? 'deposit' : 'works',
    amountGross,
    amountNet,
    vatAmount: money(amountGross - amountNet),
    vatEnabled,
    title: kind === 'one_off' ? arrangement.title : `${label} — ${arrangement.title}`,
    lineName: String(args.lineName || '').trim() || (kind === 'one_off' ? arrangement.title : label),
    sourceEvent: `arrangement:${arrangement.id}:${kind}:${sequence}`,
    frozen: kind === 'deposit',
    ...(cis ? { cis } : {}),
  };
}

/** CIS the customer deducts: labour (ex VAT) at the band's rate. Null when CIS does not apply. */
export function invoiceCis(args: {
  band: CisBand;
  amountNet: number;
  amountGross: number;
  labourNet?: number | null;
}): InvoiceCis | null {
  if (args.band === 'none') return null;
  const rate = CIS_BAND_RATES[args.band] as number;
  const labourNet = args.labourNet == null || (args.labourNet as unknown) === '' ? args.amountNet : money(Number(args.labourNet));
  if (!(labourNet >= 0) || labourNet > args.amountNet + 0.005) {
    throw new Error('The labour amount must be between zero and the invoice amount (ex VAT).');
  }
  const deduction = money(labourNet * rate);
  return {
    band: args.band,
    rate,
    labourNet,
    deduction,
    payableAfterCis: money(args.amountGross - deduction),
  };
}

/** CIS wording printed in the invoice notes. */
export function cisNote(cis: InvoiceCis): string {
  const pct = Math.round(cis.rate * 100);
  if (cis.deduction === 0) {
    return `CIS: Keystone holds gross payment status, so no CIS deduction applies (labour ${formatGbp(cis.labourNet)}).`;
  }
  return `CIS: deduct ${formatGbp(cis.deduction)} (${pct}% of labour ${formatGbp(cis.labourNet)}). Amount payable after CIS: ${formatGbp(cis.payableAfterCis)}.`;
}

/** VAT wording printed in the invoice notes when the invoice does not charge VAT normally. */
export function vatTreatmentNote(args: {
  treatment: VatTreatment;
  amountNet: number;
  vatPct: number;
}): string | null {
  if (args.treatment === 'reverse_charge') {
    const pct = Math.round(args.vatPct * 1000) / 10;
    const vat = money(args.amountNet * args.vatPct).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
    return `Reverse charge: customer to pay the VAT to HMRC. VAT at ${pct}% to be accounted for by the customer: ${vat}.`;
  }
  if (args.treatment === 'exempt') return 'This supply is exempt from VAT.';
  if (args.treatment === 'outside_scope') return 'This supply is outside the scope of UK VAT.';
  return null;
}

export function formatGbp(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
}

/** Once an arrangement has live invoices, these must stay as they were invoiced. */
export function arrangementEditLocked(invoices: LedgerInvoice[]): boolean {
  return invoices.some(isLive);
}

/**
 * Checks an edit to an existing arrangement. Title, description and terms can always change.
 * Account, VAT and deposit are fixed once invoices exist, and the agreed amount cannot drop
 * below what has already been invoiced.
 */
export function validateArrangementEdit(
  arrangement: BillingArrangement,
  input: BillingArrangementInput,
  invoices: LedgerInvoice[],
): string | null {
  const problem = validateArrangementInput(input);
  if (problem) return problem;
  const ledger = arrangementLedger(arrangement, invoices);
  if (arrangementEditLocked(invoices)) {
    if (input.accountId !== arrangement.accountId) return 'The account cannot change once invoices have been raised.';
    const vatPct = input.vatPct ?? 0.2;
    if (normaliseVatTreatment(input.vatTreatment) !== arrangement.vatTreatment || Math.abs(vatPct - arrangement.vatPct) > 0.00001) {
      return 'VAT cannot change once invoices have been raised.';
    }
    const deposit = input.depositPercentage ?? null;
    const sameDeposit = deposit == null || arrangement.depositPercentage == null
      ? deposit === arrangement.depositPercentage
      : Math.abs(deposit - arrangement.depositPercentage) < 0.00001;
    if (!sameDeposit) return 'The deposit cannot change once invoices have been raised.';
    if (normaliseCisBand(input.cisBand) !== arrangement.cisBand) return 'CIS cannot change once invoices have been raised.';
  }
  if (arrangement.nextPeriodIndex > 0) {
    if (normaliseFrequency(input.frequency) !== arrangement.frequency) {
      return 'The frequency cannot change once a period has been invoiced. Close this arrangement and start a new one.';
    }
    if ((input.firstPeriodStart || null) !== arrangement.firstPeriodStart) {
      return 'The first period start cannot change once a period has been invoiced.';
    }
  }
  if (input.agreedNet != null) {
    const agreedGross = grossFromNet(Number(input.agreedNet), normaliseVatTreatment(input.vatTreatment), input.vatPct ?? 0.2);
    if (agreedGross < ledger.invoicedGross - 0.005) {
      return `${formatGbp(ledger.invoicedGross)} has already been invoiced, so the agreed amount cannot be lower.`;
    }
  }
  return null;
}

/**
 * What an arrangement invoice was raised as. Interim and balance invoices share the `works`
 * ledger role, so the kind is read from the source marker written when it was issued.
 */
export function arrangementInvoiceLabel(sourceEvent: string | null | undefined, role: string | null | undefined): string {
  const kind = String(sourceEvent || '').split(':')[2] as ArrangementInvoiceKind | undefined;
  if (kind && kind in ARRANGEMENT_INVOICE_LABELS) return ARRANGEMENT_INVOICE_LABELS[kind];
  const normalized = normalizeInvoiceRole(role);
  if (normalized === 'deposit') return 'Deposit';
  if (normalized === 'credit') return 'Credit';
  return 'Invoice';
}

/**
 * What an invoice covers, for the invoice list: the period dates for recurring invoices,
 * otherwise what it was raised as (one-off, deposit, interim, balance).
 */
export function arrangementInvoiceCoverage(
  arrangement: Pick<BillingArrangement, 'frequency' | 'firstPeriodStart'>,
  sourceEvent: string | null | undefined,
  role: string | null | undefined,
): string {
  const parts = String(sourceEvent || '').split(':');
  if (parts[0] === 'arrangement' && parts[2] === 'recurring') {
    const index = Number(parts[3]);
    if (arrangement.frequency !== 'none' && arrangement.firstPeriodStart && Number.isInteger(index) && index >= 0) {
      return periodLabel(billingPeriod(arrangement.firstPeriodStart, arrangement.frequency, index));
    }
    return parts[4] ? `Period from ${parts[4]}` : 'Recurring';
  }
  if (parts[0] === 'arrangement' && parts[2] === 'one_off') return 'One-off';
  return arrangementInvoiceLabel(sourceEvent, role);
}

/**
 * Why an arrangement cannot be deleted, or null when it can. Only arrangements whose invoices
 * are all drafts that never went to FreeAgent can go; anything issued is kept for the record.
 */
export function arrangementDeleteBlock(
  invoices: Array<{ status?: string | null; freeagent_invoice_url?: string | null }>,
): string | null {
  const issued = invoices.filter((invoice) => (
    String(invoice.status || '').trim().toLowerCase() !== 'draft' || !!invoice.freeagent_invoice_url
  ));
  if (issued.length === 0) return null;
  return `It has ${issued.length} invoice${issued.length === 1 ? '' : 's'} that ${issued.length === 1 ? 'has' : 'have'} been issued or sent to FreeAgent. Close it instead, or void and delete those first.`;
}
